/**
 * `orange-stepper` — component-spec.md §10.
 *
 * An `<ol>` with `aria-current="step"` on the current item. The current step is
 * marked by weight and an inverse fill; done steps by a grey ground and a tick.
 * Nothing is marked by colour — a stepper where progress is green is unreadable
 * to a fifth of the audience and would be the only place hue carried meaning.
 *
 * Steps are not links. Whether jumping back to a completed step is allowed
 * depends on what happens to a verified OTP and a stock reservation when the
 * structure of the order changes, and that is undecided — so the stepper reports
 * progress and the screens own navigation.
 *
 * The step *count* is a recorded disagreement, not a decision made here: see
 * `core/flow/flow-steps.ts`.
 *
 * ### The fourth state, and why it exists
 *
 * `skipped` is not in the component spec or the prototype, both of which assume the
 * customer walks the steps in order. One path in Journey B does not: a declined
 * instalment decision treated as `OFFER_FULL_PRICE` goes from S-12 straight to S-04,
 * past "Rate", and a positional stepper then reports a step the customer never saw as
 * finalised — to a screen reader, literally "Rate — finalizat".
 *
 * So the state exists to avoid asserting something untrue, not to add a feature. Its
 * treatment is the least invented one available: the neutral ground of an unvisited
 * step, an em dash where the number or the tick would be, and the word *omis* for
 * assistive tech. No source says whether a skipped step should instead disappear from
 * the bar — recorded in `docs/SCREEN-MAP.md`.
 */
import { ChangeDetectionStrategy, Component, input } from '@angular/core';

export interface Step {
  readonly id: string;
  readonly label: string;
  readonly state: 'done' | 'current' | 'upcoming' | 'skipped';
}

@Component({
  selector: 'orange-stepper',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <ol [class.is-compact]="compact()" [attr.aria-label]="ariaLabel()">
      @for (step of steps(); track step.id; let i = $index) {
        <li
          [class.is-done]="step.state === 'done'"
          [class.is-current]="step.state === 'current'"
          [class.is-skipped]="step.state === 'skipped'"
          [attr.aria-current]="step.state === 'current' ? 'step' : null"
        >
          <span class="ord" aria-hidden="true">{{ ordinal(step.state, i) }}</span>
          <span class="label">{{ step.label }}</span>
          <!-- "done", "current" and "skipped" are visual. Without this an
               assistive-tech user hears an undifferentiated list of seven words. -->
          @if (step.state === 'done') {
            <span class="sr-only" i18n="@@stepper.done">— finalizat</span>
          }
          @if (step.state === 'skipped') {
            <span class="sr-only" i18n="@@stepper.skipped">— omis</span>
          }
        </li>
      }
    </ol>
  `,
  styles: [
    `
      ol {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-1);
        margin: 0;
        padding: 0;
        list-style: none;
      }

      li {
        display: inline-flex;
        align-items: center;
        gap: var(--space-1);
        font-size: 11px;
        line-height: 16px;
        font-weight: 700;
        padding: var(--space-1) var(--space-2);
        border: var(--border-hairline) solid var(--grey-400);
        background: var(--surface);
        color: var(--grey-700);
      }

      li.is-done {
        background: var(--grey-300);
        border-color: var(--grey-300);
        color: var(--ink);
      }

      li.is-current {
        background: var(--surface-inverse);
        border-color: var(--surface-inverse);
        color: var(--surface);
      }

      /* Skipped keeps the unvisited ground — it is not progress — and is told
         apart from an upcoming step by the em dash in place of its number. */
      li.is-skipped .label {
        color: var(--grey-700);
      }

      /* Compact — mobile and the MyOrange webview. The current step keeps its
         label; the rest collapse to their number, so the bar still fits on a
         390px viewport without scrolling sideways. */
      .is-compact li:not(.is-current) .label {
        display: none;
      }
    `,
  ],
})
export class StepperComponent {
  readonly steps = input.required<readonly Step[]>();
  readonly compact = input(false);
  readonly ariaLabel = input($localize`:@@stepper.label:Pașii comenzii`);

  /**
   * The marker before the label. A skipped step keeps neither the tick — it was not
   * completed — nor its number, which in compact mode is all that remains of it and
   * would read as a step still to come.
   */
  ordinal(state: Step['state'], index: number): string {
    if (state === 'done') return '✓';
    if (state === 'skipped') return '—';
    return `${index + 1}.`;
  }
}
